/* eslint-disable react/no-unused-prop-types */
import React, { HTMLInputTypeAttribute, MouseEventHandler, ReactNode } from 'react'
import { RegisterOptions, useFormContext, UseFormRegisterReturn } from 'react-hook-form'
import './MVPTextInput.scss'

export interface MVPInputOnlyProps {
	placeholder?: string
	type?: HTMLInputTypeAttribute
	// eslint-disable-next-line react/no-unused-prop-types
	formRegistration?: UseFormRegisterReturn<any>
	onValueChanged?: (value: string) => void
	value?: string
	disabled?: boolean
	defaultValue?: string
	hasError?: boolean
	prefix?: string
	onClick?: MouseEventHandler<HTMLInputElement>
}

export const MVPInputOnly = ({
	value,
	disabled,
	placeholder,
	type,
	formRegistration,
	onValueChanged,
	defaultValue,
	hasError,
	prefix,
	onClick,
}: MVPInputOnlyProps) => {
	const handleValueChange = onValueChanged
		? (e: React.ChangeEvent<HTMLInputElement>) => {
			if (onValueChanged) onValueChanged!(e.target.value)
		}
		: undefined

	const reg = formRegistration ?? {}

	return (
		<div className={`mvp-input-box ${hasError ? 'invalid' : ''}`}>
			<span className="prefix">{prefix}</span>
			<input
				value={value}
				className='mvp-input'
				type={type}
				placeholder={placeholder}
				onInput={handleValueChange}
				{...reg}
				disabled={disabled}
				defaultValue={defaultValue}
				onClick={onClick}
			/>
		</div>
	)
}

export interface MVPInputLabelProps {
	id?: string
	children?: ReactNode | ReactNode[]
}

export const MVPInputLabel = ({ id, children }: MVPInputLabelProps) => {
	return (
		<h3 id={id} className='mvp-input-label'>
			{children}
		</h3>
	)
}

export interface MVPTextInputProps extends MVPInputOnlyProps {
	id?: string
	label?: string | ReactNode[] | ReactNode
	formField?: string
	formValidation?: RegisterOptions
	onValueChanged?: (value: string) => void
	error?: string
	value?: string
	disabled?: boolean
}

export const MVPTextInput = ({
	id,
	value,
	label,
	placeholder,
	type,
	formField,
	formValidation,
	onValueChanged,
	error,
	disabled,
	defaultValue,
	prefix,
	onClick,
}: MVPTextInputProps) => {
	let formRegistration: UseFormRegisterReturn<any> | undefined

	let hasError = error !== undefined
	let errorMessage = error

	if (formField) {
		// This is allowed because it will always be called in the same order between re-renders
		// eslint-disable-next-line react-hooks/rules-of-hooks
		const form = useFormContext()
		formRegistration = form.register(formField!, formValidation)

		if (!hasError) {
			errorMessage = form.formState.errors[formField!]?.message as string
			hasError = errorMessage !== undefined
		}
	}

	return (
		<div id={id} className='mvp-text-input'>
			<MVPInputLabel>{label}</MVPInputLabel>
			<MVPInputOnly
				value={value}
				placeholder={placeholder}
				type={type}
				formRegistration={formRegistration}
				onValueChanged={onValueChanged}
				disabled={disabled}
				defaultValue={defaultValue}
				prefix={prefix}
				hasError={hasError}
				onClick={onClick}
			/>
			<span className='mvp-text-input-error-label'>{errorMessage}</span>
		</div>
	)
}
